// src/components/MarshallArea.js - Dancer Check-in for Marshall Area
import React, { useState, useEffect, useCallback } from 'react';
import { useCompetition } from '../competition/CompetitionContext';
import { getDancersList, normalizeDancer } from '../utils/fieldCompatibility';
import { getEventsWithStartTimes, formatTime } from '../utils/startTimeCalculator';
import './MarshallArea.css';

const _host = 'chappie-demo.novosense.africa';
const _company = 'wdsf';

function MarshallArea() {
  const { selectedCompetition } = useCompetition();
  const [events, setEvents] = useState([]);
  const [competitionStartTime, setCompetitionStartTime] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showDancersModal, setShowDancersModal] = useState(false);
  const [selectedEventForDancers, setSelectedEventForDancers] = useState(null);
  const [showCheckInConfirmation, setShowCheckInConfirmation] = useState(false);
  const [selectedDancerForCheckIn, setSelectedDancerForCheckIn] = useState(null);
  const [checkedInDancers, setCheckedInDancers] = useState(new Set());

  // Fetch competition details including start time
  const fetchCompetitionDetails = async () => {
    if (!selectedCompetition) return;

    try {
      const response = await fetch(`https://${_host}:6003/api/${_company}/competitions/${selectedCompetition.id}`);
      if (response.ok) {
        const competition = await response.json();

        // Check for different possible field names
        const startTimeField = competition.official_start_time || competition.officialStartTime || competition.start_time;

        if (startTimeField) {
          const startTime = new Date(startTimeField);
          setCompetitionStartTime(startTime);
        } else {
          // Use default start time: 9:00 AM today
          const defaultStartTime = new Date();
          defaultStartTime.setHours(9, 0, 0, 0);
          setCompetitionStartTime(defaultStartTime);
        }
      } else {
        // Use default start time: 9:00 AM today
        const defaultStartTime = new Date();
        defaultStartTime.setHours(9, 0, 0, 0);
        setCompetitionStartTime(defaultStartTime);
      }
    } catch (error) {
      console.error('Marshall Area - Error fetching competition start time:', error);
      // Use default start time: 9:00 AM today
      const defaultStartTime = new Date();
      defaultStartTime.setHours(9, 0, 0, 0);
      setCompetitionStartTime(defaultStartTime);
    }
  };

  // Fetch events (filtered by selected competition)
  const fetchEvents = async () => {
    try {
      const response = await fetch(`https://${_host}:6003/api/${_company}/events`);
      if (!response.ok) {
        throw new Error('Failed to fetch events');
      }
      const data = await response.json();

      // Filter events by selected competition if available
      let filteredEvents = data;
      if (selectedCompetition) {
        filteredEvents = data.filter(event =>
          event.competitionId === selectedCompetition.id ||
          event.competition_id === selectedCompetition.id
        );
      }

      console.log(`MarshallArea: Found ${data.length} total events, ${filteredEvents.length} filtered for competition:`, selectedCompetition?.name || 'All');

      // Also fetch competition details for start time
      fetchCompetitionDetails();

      setEvents(filteredEvents);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching events:', err);
      setError('Failed to load events. Please try again later.');
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents();
    const interval = setInterval(fetchEvents, 5000);
    return () => clearInterval(interval);
  }, [selectedCompetition, fetchEvents]); // Re-fetch when selected competition changes

  // Sort events by item number
  const sortedEvents = [...events].sort((a, b) => {
    const itemA = a.itemNumber || 999;
    const itemB = b.itemNumber || 999;
    return itemA - itemB;
  });

  // Calculate start times if competition start time is available
  const eventsWithStartTimes = competitionStartTime
    ? getEventsWithStartTimes(sortedEvents, competitionStartTime)
    : sortedEvents;

  // Filter events based on search query
  const filteredEvents = eventsWithStartTimes.filter(event => {
    if (!searchQuery.trim()) return true;

    const query = searchQuery.toLowerCase().trim();

    // Search by item number
    const itemNumber = (event.itemNumber || sortedEvents.indexOf(event) + 1).toString();
    if (itemNumber.includes(query)) return true;

    // Search by event name
    if (event.name && event.name.toLowerCase().includes(query)) return true;

    // Search by round type
    if (event.roundType && event.roundType.toLowerCase().includes(query)) return true;

    return false;
  });

  const handleItemNumberClick = (event, e) => {
    e.stopPropagation();
    setSelectedEventForDancers(event);
    setShowDancersModal(true);
  };

  const handleCloseModal = () => {
    setShowDancersModal(false);
    setSelectedEventForDancers(null);
  };

  const handleDancerClick = (dancer, e) => {
    e.stopPropagation();

    // Convert dancer to normalized format for consistent field access
    const normalizedDancer = normalizeDancer(dancer);
    console.log('*** DANCER CLICK FUNCTION CALLED ***');
    console.log('Dancer clothNumber:', normalizedDancer?.clothNumber);
    console.log('Dancer name:', normalizedDancer?.name);
    console.log('Registration check-in status:', normalizedDancer?.registrationCheckin);

    // Check if dancer is already checked in for Marshall
    if (normalizedDancer.marshallCheckin) {
      alert('This dancer is already checked in for Marshall Area.');
      return;
    }

    // REGISTRATION CHECK-IN VALIDATION - Block check-in if registration is not complete
    if (!normalizedDancer.registrationCheckin || normalizedDancer.registrationCheckin === false) {
      alert('Cannot check in dancer: Registration check-in not completed. Please complete administration check-in first.');
      return;
    }

    console.log('Registration check-in passed - showing confirmation dialog');
    setSelectedDancerForCheckIn(normalizedDancer);
    setShowCheckInConfirmation(true);
  };

  const handleConfirmCheckIn = async () => {
    if (!selectedDancerForCheckIn || !selectedEventForDancers) return;

    try {
      // Create a unique identifier for this dancer in this event
      const dancerId = `${selectedEventForDancers.id}-${selectedDancerForCheckIn.id}`;

      // Here you would implement the actual check-in logic
      console.log(`Checking in dancer ${selectedDancerForCheckIn.name} (${selectedDancerForCheckIn.clothNumber}) for Marshall`);

      // Use the most reliable ID available - normalized or original
      const apiId = selectedDancerForCheckIn.id ||
                      selectedDancerForCheckIn._original?.id ||
                      selectedDancerForCheckIn._original?.couple_id ||
                      selectedDancerForCheckIn._original?.coupleId;

      // Update dancer's Marshall check-in status using the couples API
      const response = await fetch(`https://${_host}:6003/api/${_company}/couples/${apiId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          marshallCheckin: true,
          marshallCheckinTime: new Date().toISOString()
        })
      });

      if (response.ok) {
        // Mark dancer as checked in
        setCheckedInDancers(prev => new Set(prev).add(dancerId));

        // Show success message
        const successMessage = document.createElement('div');
        successMessage.className = 'check-in-success-message';
        successMessage.textContent = `✓ ${selectedDancerForCheckIn.name} (${selectedDancerForCheckIn.clothNumber}) checked in successfully!`;
        successMessage.style.cssText = `
          position: fixed;
          top: 20px;
          right: 20px;
          background: linear-gradient(135deg, #4caf50 0%, #45a049 100%);
          color: white;
          padding: 15px 20px;
          border-radius: 8px;
          box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3);
          z-index: 2000;
          font-weight: 600;
          animation: slideInRight 0.3s ease-out;
        `;
        document.body.appendChild(successMessage);

        // Remove success message after 3 seconds
        setTimeout(() => {
          if (document.body.contains(successMessage)) {
            document.body.removeChild(successMessage);
          }
        }, 3000);
      }

      setShowCheckInConfirmation(false);
      setSelectedDancerForCheckIn(null);
    } catch (error) {
      console.error('Error checking in dancer:', error);
      alert('Failed to check in dancer. Please try again.');
      setShowCheckInConfirmation(false);
      setSelectedDancerForCheckIn(null);
    }
  };

  const handleCancelCheckIn = () => {
    setShowCheckInConfirmation(false);
    setSelectedDancerForCheckIn(null);
  };

  return (
    <div className="marshall-area-container">
      <div className="page-header">
        <h1>Marshall Area</h1>
        {selectedCompetition && (
          <p className="page-subtitle">{selectedCompetition.name}</p>
        )}
      </div>

      {error && <div className="error-banner">{error}</div>}

      {loading ? (
        <div className="loading-message">Loading events...</div>
      ) : events.length === 0 ? (
        <div className="empty-message">
          <p>No events available.</p>
          <p className="hint">Events must be created in Administration first.</p>
        </div>
      ) : (
        <div className="marshall-area-content">
          <div className="events-table-container">
            <div className="events-table-header">
              <div className="table-title">
                <h2>Event Check-in Status</h2>
                <span className="event-count">
                  {searchQuery ? `${filteredEvents.length} of ${sortedEvents.length} events` : `${sortedEvents.length} events`}
                </span>
              </div>
              <div className="search-container">
                <input
                  type="text"
                  placeholder="Search by item number, event name, or round type..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="search-input"
                />
                {searchQuery && (
                  <button
                    className="search-clear-btn"
                    onClick={() => setSearchQuery('')}
                    title="Clear search"
                  >
                    ×
                  </button>
                )}
              </div>
            </div>
            <div className="events-table-scrollable">
              <table className="events-table">
                <thead>
                  <tr>
                    <th>Item #</th>
                    <th>Event Name</th>
                    <th>Round Type</th>
                    <th>Heats</th>
                    <th>Dancers</th>
                    <th>Start Time</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredEvents.map((event) => (
                    <tr key={event.id}>
                      <td>
                        <span
                          className="item-number-badge clickable"
                          onClick={(e) => handleItemNumberClick(event, e)}
                          title="Click to view dancers"
                        >
                          {event.itemNumber || '-'}
                        </span>
                      </td>
                      <td>
                        <div className="event-name-cell">
                          <div className="event-name">{event.name}</div>
                          <div className="event-meta">
                            {event.dances?.length || 0} dances
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className={`round-badge ${event.roundType === 'TBD' ? 'tbd' : ''}`}>
                          {event.roundType || 'TBD'}
                        </span>
                      </td>
                      <td>
                        <span className="heats-display">{event.numHeats || 1}</span>
                      </td>
                      <td>
                        <span className="dancers-count">
                          {event.dancers?.length || 0}
                        </span>
                      </td>
                      <td>
                        {event.startTime && (
                          <span className="event-start-time" title="Estimated start time">
                            🕐 {formatTime(event.startTime)}
                          </span>
                        )}
                      </td>
                      <td>
                        <span className={`status-badge ${event.broadcast_state === 'live' ? 'ready' : 'pending'}`}>
                          {event.broadcast_state === 'live' ? 'Ready' : 'Pending'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filteredEvents.length === 0 && searchQuery && (
                <div className="no-search-results">
                  <p>No events found matching "{searchQuery}"</p>
                  <button
                    className="clear-search-btn"
                    onClick={() => setSearchQuery('')}
                  >
                    Clear search
                  </button>
                </div>
              )}
            </div>
          </div>

  
          {/* Dancers Modal */}
          {showDancersModal && selectedEventForDancers && (
            <div className="modal-overlay" onClick={handleCloseModal}>
              <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h3>Dancers in Event</h3>
                  <button className="modal-close-btn" onClick={handleCloseModal}>×</button>
                </div>
                <div className="modal-event-info">
                  <span className="modal-item-number">{selectedEventForDancers.itemNumber}</span>
                  <span className="modal-event-name">{selectedEventForDancers.name}</span>
                  <span className="modal-round-type">{selectedEventForDancers.roundType || 'TBD'}</span>
                </div>
                <div className="modal-body">
                  {selectedEventForDancers.dancers && selectedEventForDancers.dancers.length > 0 ? (
                    <>
                      <div className="dancers-header">
                        <span className="dancers-count-header">
                          {selectedEventForDancers.dancers.length} Dancer{selectedEventForDancers.dancers.length !== 1 ? 's' : ''}
                        </span>
                      </div>
                      <div className="modal-dancers-list">
                        {selectedEventForDancers.dancers.map((dancer, index) => {
                          // Debug: Log the raw dancer data to see what fields are available
                          console.log('Raw dancer data:', dancer);

                          // Normalize dancer data to ensure consistent field names
                          const normalizedDancer = normalizeDancer(dancer);
                          console.log('Normalized dancer data:', normalizedDancer);

                          const dancerId = `${selectedEventForDancers.id}-${normalizedDancer.id}`;
                          const isCheckedIn = checkedInDancers.has(dancerId) || normalizedDancer.marshallCheckin;
                          const canCheckIn = normalizedDancer.registrationCheckin !== false;

                          return (
                            <div
                              key={index}
                              className={`modal-dancer-item ${canCheckIn ? 'clickable' : 'disabled'} ${isCheckedIn ? 'checked-in' : ''}`}
                              onClick={(e) => handleDancerClick(normalizedDancer, e)}
                              title={
                                isCheckedIn ? "Checked in for Marshall" :
                                !canCheckIn ? "Cannot check in - registration not completed" :
                                "Click to check in dancer"
                              }
                            >
                              <div className="modal-dancer-number">{normalizedDancer.clothNumber}</div>
                              <div className="modal-dancer-info">
                                <div className="modal-dancer-name">{normalizedDancer.name}</div>
                                <div className="modal-dancer-details">
                                  <span className="modal-dancer-type">{normalizedDancer.dancerType || 'Solo'}</span>
                                  {normalizedDancer.country && (
                                    <span className="modal-dancer-country">• {normalizedDancer.country}</span>
                                  )}
                                  {!canCheckIn && (
                                    <span className="registration-warning" title="Registration check-in not completed">⚠️ Not Registered</span>
                                  )}
                                </div>
                              </div>
                              <div className="check-in-indicator">
                                <span className="check-in-icon">{isCheckedIn ? '✓' : ''}</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </>
                  ) : (
                    <div className="modal-empty-message">
                      <p>No dancers allocated to this event</p>
                      <span className="modal-hint">Dancers need to be allocated from the Administration section</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Check-in Confirmation Modal */}
          {showCheckInConfirmation && selectedDancerForCheckIn && (
            <div className="modal-overlay check-in-overlay" onClick={handleCancelCheckIn}>
              <div className="check-in-modal" onClick={(e) => e.stopPropagation()}>
                <div className="check-in-header">
                  <div className="check-in-icon-large">👤</div>
                  <h3>Confirm Check-in</h3>
                  <button className="check-in-close-btn" onClick={handleCancelCheckIn}>×</button>
                </div>

                <div className="check-in-content">
                  <div className="check-in-dancer-info">
                    <div className="check-in-dancer-number">{selectedDancerForCheckIn.clothNumber}</div>
                    <div className="check-in-dancer-details">
                      <div className="check-in-dancer-name">{selectedDancerForCheckIn.name}</div>
                      <div className="check-in-dancer-meta">
                        <span className="check-in-dancer-type">{selectedDancerForCheckIn.type || 'Solo'}</span>
                        {selectedDancerForCheckIn.country && (
                          <span className="check-in-dancer-country">• {selectedDancerForCheckIn.country}</span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="check-in-event-info">
                    <strong>Event:</strong> {selectedEventForDancers.name} (#{selectedEventForDancers.itemNumber})
                  </div>

                  <div className="check-in-confirmation">
                    <p>Are you sure you want to check in this dancer for Marshall?</p>
                    <div className="check-in-actions">
                      <button className="check-in-cancel-btn" onClick={handleCancelCheckIn}>
                        Cancel
                      </button>
                      <button className="check-in-confirm-btn" onClick={handleConfirmCheckIn}>
                        ✓ Confirm Check-in
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default MarshallArea;
